/**
 * GEN-8 — "this is a cached copy, and here's why it isn't refreshing."
 *
 * Every admin surface loads cache-first (stores/useCachedResource + a few tabs
 * that do it by hand). That is CORRECT: showing the last-known data beats a
 * spinner. What was wrong is that a failed refresh over a non-empty cache was
 * swallowed entirely (`if (!cached) setError(...)`), so a super-admin whose token
 * expired, whose role changed, or whose backend was down kept reading up-to-7-day
 * old numbers with no error, no age and no hint — while the 60s poll kept firing
 * into the void.
 *
 * Deliberately unobtrusive and built from the EXISTING visual language (badge +
 * .faint line) — the operator should notice it without it shouting over the data.
 */
import type { Staleness } from "../stores/useCachedResource.js";

/** Coarse age of the displayed copy ("4m", "2h", "3d"). */
function fmtAge(cachedAt: number | null): string {
  if (cachedAt == null) return "an unknown time ago";
  const mins = Math.max(0, (Date.now() - cachedAt) / 60_000);
  if (mins < 1) return "moments ago";
  if (mins < 60) return `${Math.round(mins)}m ago`;
  const hrs = mins / 60;
  if (hrs < 24) return `${Math.round(hrs)}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export function StaleNotice({
  stale,
  testid = "stale-notice",
}: {
  stale: Staleness | null;
  /** Override when a surface renders more than one notice. */
  testid?: string;
}) {
  if (!stale) return null;
  return (
    <p className="faint stale-notice" role="status" data-testid={testid}>
      <span className="badge badge--warn">Stale</span>{" "}
      Showing cached data from {fmtAge(stale.cachedAt)} — last refresh failed: {stale.reason}
    </p>
  );
}
